/* eslint-disable react/function-component-definition */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { TextField, useTheme, useMediaQuery, IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import Swal from "sweetalert2";
import api from "api/api";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";

import useAccountMembersheetData, { parseNumber, formatNumber } from "./accountMemberSheetData";
import LoadingScreen from "layouts/loading/loadingscreen";
import { API_BASE_URL } from "config";
import { buildFileDownloadUrl } from "utils/fileDownloadUrl";
import { canEditSensitiveField, maskSensitiveFieldValue } from "utils/maskingUtils";
import PreviewOverlay from "utils/PreviewOverlay";
import {
  HEADOFFICE_DOCUMENT_FILE_ACCEPT,
  getHeadOfficeDocumentPreviewKind,
  isHeadOfficeDocumentSupportedFile,
} from "utils/headOfficeDocumentImageUtils";

// 인사 -> 채용관리 -> 현장 직원목록
function AccountMemberSheet() {

  // =========================
  // ✅ 통합이면 account_id=1 강제
  // =========================
  const INTEGRATION_POSITION = "7";
  const INTEGRATION_ACCOUNT_ID = "1";

  // =========================
  // ✅ 유틸이면 account_id=2 강제
  // =========================
  const UTIL_POSITION = "6";
  const UTIL_ACCOUNT_ID = "2"; // ✅ 유틸이면 account_id는 2로 저장/표시

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accountInput, setAccountInput] = useState("");
  const [memberInput, setMemberInput] = useState("");
  const [memberSearchName, setMemberSearchName] = useState("");
  const [activeStatus, setActiveStatus] = useState("N");
  const accountInitRef = useRef(false);
  // ✅ 직원 검색 직후 거래처 Autocomplete의 자동 reset 재입력을 막기 위한 플래그
  const suppressAccountResetRef = useRef(false);
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTabletOrSmallDesktop = useMediaQuery("(max-width:1280px)");
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [maskingEnabled, setMaskingEnabled] = useState(true);

  const {
    activeRows,
    setActiveRows,
    originalRows,
    setOriginalRows,
    accountList,
    workSystemList,
    originalWorkSystemList,
    fetchWorkSystemList,
    saveWorkSystemList,
    saveData,
    fetchAccountMembersAllList,
    loading: hookLoading,
  } = useAccountMembersheetData(selectedAccountId, activeStatus, memberSearchName);

  const [loading, setLoading] = useState(true);
  // 열 너비 고정 상태
  const [isColumnWidthLocked, setIsColumnWidthLocked] = useState(false);
  // 자동 측정 열 폭 캐시
  const [measuredColWidths, setMeasuredColWidths] = useState({});
  // 마스킹 전환 재고정 플래그
  const [relockAfterMeasure, setRelockAfterMeasure] = useState(false);

  // =========================
  // ✅ 근무형태 관리 Modal 상태
  // =========================
  const [wsOpen, setWsOpen] = useState(false);
  const [wsRows, setWsRows] = useState([]);
  const [wsOriginal, setWsOriginal] = useState([]);
  // ✅ 근무형태 모달 우클릭 컨텍스트 메뉴 상태
  const [wsCtxMenu, setWsCtxMenu] = useState({ open: false, mouseX: 0, mouseY: 0, rowIndex: null, idx: null });

  const numericCols = ["salary"];

  // =========================
  // ✅ 이미지 업로드/뷰어 기능
  // =========================
  const imageFields = ["employment_contract", "id", "bankbook"];
  const previewFieldLabels = {
    employment_contract: "근로계약서",
    id: "신분증",
    bankbook: "통장사본",
  };
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewObjectUrlsRef = useRef([]);
  const fileIconSx = { color: "#1e88e5" };

  const clearPreviewObjectUrls = useCallback(() => {
    previewObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewObjectUrlsRef.current = [];
  }, []);

  const getPreviewKind = useCallback(
    (value) =>
      typeof value === "object"
        ? getHeadOfficeDocumentPreviewKind(value)
        : getHeadOfficeDocumentPreviewKind({ image_path: value }),
    []
  );

  const toPreviewUrl = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "object") {
      const objectUrl = URL.createObjectURL(value);
      previewObjectUrlsRef.current.push(objectUrl);
      return objectUrl;
    }
    const path = String(value || "");
    if (/^(https?:\/\/|blob:|data:)/i.test(path)) return path;
    const base = String(API_BASE_URL || "").replace(/\/+$/, "");
    const params = new URLSearchParams();
    params.set("file_path", path.startsWith("/") ? path : `/${path}`);
    return `${base}/Account/AccountStoredFileView?${params.toString()}`;
  }, []);

  const handleViewImage = useCallback(
    (rowData, field) => {
      clearPreviewObjectUrls();
      const files = imageFields
        .map((key) => {
          const value = rowData?.[key];
          if (!value) return null;
          return {
            url: toPreviewUrl(value),
            name: previewFieldLabels[key],
            kind: getPreviewKind(value),
          };
        })
        .filter(Boolean);

      const targetIndex = Math.max(0, files.findIndex((file) => file.name === previewFieldLabels[field]));
      setPreviewFiles(files);
      setPreviewIndex(targetIndex);
    },
    [clearPreviewObjectUrls, getPreviewKind, imageFields, previewFieldLabels, toPreviewUrl]
  );

  const handleCloseViewer = useCallback(() => {
    setPreviewFiles([]);
    setPreviewIndex(0);
    clearPreviewObjectUrls();
  }, [clearPreviewObjectUrls]);

  const handleDownload = useCallback((path) => {
    if (!path || typeof path !== "string") return;
    const url = buildFileDownloadUrl(path);
    const filename = path.split("/").pop() || "download";

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const normalizeTime = (t) => {
    if (!t) return "";
    return String(t)
      .trim()
      .replace(/^0(\d):/, "$1:");
  };

  const keepEditableTailVisible = useCallback((el) => {
    if (!el) return;
    window.requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
    });
  }, []);

  const lockColumnsForEditing = useCallback(() => {
    setIsColumnWidthLocked((prev) => (prev ? prev : true));
  }, []);

  // hygiene와 동일하게 OperateImgUpload 사용
  const uploadImage = async (file, field, row) => {
    const formData = new FormData();
    formData.append("file", file);

    formData.append("type", "member");
    const gubun = `${field}_${row.member_id || row.rrn || Date.now()}`;
    formData.append("gubun", gubun);
    formData.append("folder", row.account_id || selectedAccountId || "common");

    const res = await api.post("/Operate/OperateImgUpload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (res.data.code === 200) {
      return res.data.image_path;
    }
    throw new Error(res.data.message || "이미지 업로드 실패");
  };

  // =========================
  // ✅ 통합/유틸이면 account_id 강제 (저장/행추가/직책변경 공통 보정)
  // =========================
  const applyUtilAccountId = useCallback(
    (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((r) => {
        const pos = String(r?.position_type ?? "");
        const acc = r?.account_id;

        // 통합이면 무조건 1
        if (pos === INTEGRATION_POSITION) {
          return { ...r, account_id: INTEGRATION_ACCOUNT_ID };
        }

        // 유틸이면 무조건 2
        if (pos === UTIL_POSITION) {
          return { ...r, account_id: UTIL_ACCOUNT_ID };
        }

        // 통합/유틸이 아닌데 account_id가 1/2로 남아있으면 selectedAccountId로 복구
        if (String(acc ?? "") === INTEGRATION_ACCOUNT_ID) {
          return { ...r, account_id: selectedAccountId || (accountList?.[0]?.account_id ?? "") };
        }

        // 유틸이 아닌데 account_id가 2로 남아있으면 selectedAccountId로 복구(원하면 이 로직 제거 가능)
        if (String(acc ?? "") === UTIL_ACCOUNT_ID) {
          return { ...r, account_id: selectedAccountId || (accountList?.[0]?.account_id ?? "") };
        }

        return r;
      });
    },
    [
      INTEGRATION_ACCOUNT_ID,
      INTEGRATION_POSITION,
      UTIL_ACCOUNT_ID,
      UTIL_POSITION,
      selectedAccountId,
      accountList,
    ]
  );

  useEffect(() => {
    if (accountInitRef.current) return;
    if (selectedAccountId) {
      accountInitRef.current = true;
      return;
    }
    if (!Array.isArray(accountList) || accountList.length === 0) return;
    setSelectedAccountId(String(accountList[0].account_id));
    accountInitRef.current = true;
  }, [accountList, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId && !memberSearchName) {
      setLoading(false);
      return;
    }

    setIsColumnWidthLocked(false);
    setLoading(true);
    Promise.resolve(fetchAccountMembersAllList()).finally(() => setLoading(false));
  }, [selectedAccountId, activeStatus, memberSearchName]);

  // 합계 계산 (현재 화면에서는 사실상 의미 없지만 기존 유지)
  const calculateTotal = (row) => {
    const breakfast = parseNumber(row.breakfast);
    const lunch = parseNumber(row.lunch);
    const dinner = parseNumber(row.dinner);
    const ceremony = parseNumber(row.ceremony);
    const avgMeals = (breakfast + lunch + dinner) / 3;
    return Math.round(avgMeals + ceremony);
  };

  // ★★★★★ activeRows 로딩 후 초기 세팅 (유틸 account_id 보정 포함)
  useEffect(() => {
    if (activeRows && activeRows.length > 0) {
      const updated = activeRows.map((row) => ({
        ...row,
        total: calculateTotal(row),
      }));

      const fixed = applyUtilAccountId(updated);
      setActiveRows(fixed);
      setOriginalRows(fixed);
    } else {
      setOriginalRows([]);
    }
  }, [activeRows?.length]);

  // 시간 옵션
  const generateTimeOptions = (startHHMM, endHHMM, stepMinutes = 30) => {
    const toMinutes = (hhmm) => {
      const [h, m] = hhmm.split(":").map(Number);
      return h * 60 + m;
    };
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const start = toMinutes(startHHMM);
    const end = toMinutes(endHHMM);
    const arr = [];
    for (let t = start; t <= end; t += stepMinutes) {
      const hh = Math.floor(t / 60);
      const mm = t % 60;
      arr.push(`${hh}:${pad(mm)}`);
    }
    return arr;
  };
  const startTimes = generateTimeOptions("5:30", "16:00", 30);
  const endTimes = generateTimeOptions("10:00", "20:00", 30);

  const positionOptions = [
    { value: "1", label: "영양사" },
    { value: "2", label: "조리팀장" },
    { value: "3", label: "조리장" },
    { value: "4", label: "조리사" },
    { value: "5", label: "조리원" },
    { value: "6", label: "유틸" },
    { value: "7", label: "통합" },
  ];

  const contractOptions = [
    { value: "1", label: "4대보험" },
    { value: "2", label: "프리랜서" },
  ];

  const delOptions = [
    { value: "N", label: "재직" },
    { value: "Y", label: "퇴사" },
  ];

  const corOptions = [
    { value: "1", label: "(주)더채움" },
    { value: "2", label: "더채움" },
  ];

  const formatDateForInput = (val) => {
    if (!val && val !== 0) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    try {
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  const insuranceDateFieldKeys = [
    "national_pension",
    "health_insurance",
    "industrial_insurance",
    "employment_insurance",
  ];

  const getInsuranceStatus = (val) => {
    const raw = String(val ?? "").trim();
    if (!raw) return "";
    if (raw === "미해당" || raw.toUpperCase() === "N") return "N";
    return formatDateForInput(raw) ? "Y" : "N";
  };

  // ✅ 거래처 옵션(Autocomplete)
  const accountOptions = useMemo(
    () =>
      (accountList || []).map((a) => ({
        value: String(a.account_id),
        label: a.account_name,
      })),
    [accountList]
  );


  const selectedAccountOption = useMemo(() => {
    const v = String(selectedAccountId ?? "");
    return accountOptions.find((o) => o.value === v) || null;
  }, [accountOptions, selectedAccountId]);

  const selectAccountByInput = useCallback(() => {
    // ✅ 거래처 검색을 직접 수행하면 자동 reset 차단은 해제
    suppressAccountResetRef.current = false;

    // 거래처 검색이 실행되면 직원명 검색 필터는 해제
    setMemberInput("");
    setMemberSearchName("");

    const q = String(accountInput || "").trim();
    if (!q) return;
    const list = accountOptions || [];
    const qLower = q.toLowerCase();
    const exact = list.find((o) => String(o?.label || "").toLowerCase() === qLower);
    const partial =
      exact || list.find((o) => String(o?.label || "").toLowerCase().includes(qLower));
    if (partial) {
      setSelectedAccountId(partial.value);
      setAccountInput(partial.label || q);
    }
  }, [accountInput, accountOptions]);

  const selectMemberByInput = useCallback(() => {
    const q = String(memberInput || "").trim();
    // ✅ 직원 검색 직후에는 거래처 입력값 자동복원(reset)을 막음
    suppressAccountResetRef.current = true;
    // 직원 검색이 실행되면 거래처 검색 입력창은 비움
    setAccountInput("");
    setMemberInput(q);
    setMemberSearchName(q);
  }, [memberInput]);

  const handleToggleMasking = useCallback(() => {
    setMaskingEnabled((prev) => !prev);
    if (isColumnWidthLocked) {
      setRelockAfterMeasure(true);
      setIsColumnWidthLocked(false);
    }
  }, [isColumnWidthLocked]);

  const columns = useMemo(
    () => [
      { header: "구분", accessorKey: "cor_type", size: 100 },
      { header: "성명", accessorKey: "name", size: 100 },
      { header: "주민번호", accessorKey: "rrn", size: 100 },
      { header: "업장명", accessorKey: "account_id", size: 150 },
      { header: "직책", accessorKey: "position_type", size: 100 },
      { header: "계좌번호", accessorKey: "account_number", size: 150 },
      { header: "연락처", accessorKey: "phone", size: 100 },
      { header: "주소", accessorKey: "address", size: 150 },
      { header: "계약형태", accessorKey: "contract_type", size: 50 },
      { header: "실입사일", accessorKey: "act_join_dt", size: 80 },
      { header: "입사일", accessorKey: "join_dt", size: 80 },
      { header: "퇴직정산일", accessorKey: "ret_set_dt", size: 80 },
      { header: "4대보험 상실일", accessorKey: "loss_major_insurances", size: 80 },
      { header: "퇴사여부", accessorKey: "del_yn", size: 80 },
      { header: "퇴사일", accessorKey: "del_dt", size: 80 },
      { header: "퇴사사유", accessorKey: "del_note", size: 100 },
      {
        header: "급여(월)",
        accessorKey: "salary",
        size: 80,
        cell: (info) => formatNumber(info.getValue()),
      },
      { header: "근무형태", accessorKey: "idx", size: 180 },
      { header: "시작", accessorKey: "start_time", size: 60 },
      { header: "마감", accessorKey: "end_time", size: 60 },
      { header: "국민연금", accessorKey: "national_pension", size: 80 },
      { header: "건강보험", accessorKey: "health_insurance", size: 80 },
      { header: "산재보험", accessorKey: "industrial_insurance", size: 80 },
      { header: "고용보험", accessorKey: "employment_insurance", size: 80 },
      { header: "비고", accessorKey: "note", minWidth: 80, maxWidth: 150 },
      { header: "본사노트", accessorKey: "headoffice_note", minWidth: 80, maxWidth: 150 },
      { header: "지원금", accessorKey: "subsidy", minWidth: 80, maxWidth: 150 },

      { header: "근로계약서", accessorKey: "employment_contract", size: 120 },
      { header: "신분증", accessorKey: "id", size: 120 },
      { header: "통장사본", accessorKey: "bankbook", size: 120 },
    ],
    []
  );

  const table = useReactTable({
    data: activeRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (loading || isColumnWidthLocked) return;

    const rafId = window.requestAnimationFrame(() => {
      const tableEl = tableContainerRef.current?.querySelector("table");
      if (!tableEl) return;

      const headerCells = tableEl.querySelectorAll("thead th");
      if (!headerCells || headerCells.length === 0) return;

      const next = {};
      table.getFlatHeaders().forEach((header, idx) => {
        const el = headerCells[idx];
        if (!el) return;
        const width = Math.ceil(el.getBoundingClientRect().width);
        if (width > 0) next[header.id] = width;
      });

      if (Object.keys(next).length > 0) {
        setMeasuredColWidths(next);
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [loading, isColumnWidthLocked, table, activeRows, maskingEnabled]);

  useEffect(() => {
    if (!relockAfterMeasure || isColumnWidthLocked) return;
    if (Object.keys(measuredColWidths || {}).length === 0) return;

    setIsColumnWidthLocked(true);
    setRelockAfterMeasure(false);
  }, [relockAfterMeasure, isColumnWidthLocked, measuredColWidths]);

  // ✅ 저장 로직: 최종 payload에서도 유틸 account_id=2 강제
  const handleSave = async () => {
    handleCloseViewer();
    const changedRowsWithIndex = activeRows
      .map((row, idx) => ({ row, rowIndex: idx }))
      .filter(({ row, rowIndex }) => {
        const original = originalRows[rowIndex];
        if (!original) return true;

        return Object.keys(row).some((key) => {
          if (imageFields.includes(key)) {
            const v = row[key];
            const o = original[key];
            if (typeof v === "object" && v) return true;
            return String(v ?? "") !== String(o ?? "");
          }

          if (numericCols.includes(key)) {
            return Number(row[key] ?? 0) !== Number(original[key] ?? 0);
          }
          return String(row[key] ?? "") !== String(original[key] ?? "");
        });
      });
    const changedRows = changedRowsWithIndex.map((item) => item.row);

    if (changedRows.length === 0) {
      Swal.fire("저장할 변경사항이 없습니다.", "", "info");
      return;
    }

    try {
      const userId = localStorage.getItem("user_id");

      const cleanRow = (row) => {
        const newRow = { ...row };
        Object.keys(newRow).forEach((key) => {
          if (newRow[key] === "" || newRow[key] === undefined) {
            newRow[key] = null;
          }
        });
        return newRow;
      };

      const processedWithIndex = await Promise.all(
        changedRowsWithIndex.map(async ({ row, rowIndex }) => {
          const newRow = cleanRow(row);

          for (const field of imageFields) {
            if (newRow[field] && typeof newRow[field] === "object") {
              const uploadedPath = await uploadImage(newRow[field], field, newRow);
              newRow[field] = uploadedPath;
            }
          }

          return {
            rowIndex,
            row: {
              ...newRow,
              user_id: userId,
            },
          };
        })
      );

      // ✅ 여기서 한번 더 유틸 account_id=2 강제
      const processedFixed = applyUtilAccountId(processedWithIndex.map((item) => item.row));

      const res = await api.post("/Operate/AccountMembersSave", {
        data: processedFixed,
      });

      if (res.data.code === 200) {
        Swal.fire("저장 완료", "변경사항이 저장되었습니다.", "success");

        // 원본 스냅샷도 보정된 상태로 유지
        const savedRowByIndex = new Map(
          processedWithIndex.map((item, idx) => [item.rowIndex, processedFixed[idx]])
        );
        const fixedAll = applyUtilAccountId(
          activeRows.map((row, idx) => (savedRowByIndex.has(idx) ? { ...row, ...savedRowByIndex.get(idx) } : row))
        );
        setActiveRows(fixedAll);
        setOriginalRows(fixedAll.map((row) => ({ ...row })));

        await fetchAccountMembersAllList();
        setIsColumnWidthLocked(false);
      } else {
        Swal.fire("저장 실패", res.data.message || "서버 오류", "error");
      }
    } catch (err) {
      Swal.fire("저장 실패", err.message, "error");
    }
  };

  // =========================
  // ✅ 근무형태 모달 로직
  // =========================
  const openWorkSystemModal = async () => {
    const latest = await fetchWorkSystemList({ snapshot: true });
    setWsRows(latest || []);
    setWsOriginal(latest || []);
    setWsOpen(true);
  };

  const closeWorkSystemModal = () => setWsOpen(false);

  const handleWsAddRow = () => {
    const newRow = {
      idx: null,
      work_system: "",
      start_time: startTimes?.[0] ?? "5:30",
      end_time: endTimes?.[0] ?? "10:00",
    };
    setWsRows((prev) => [newRow, ...prev]);
    setWsOriginal((prev) => [newRow, ...prev]);
  };

  const handleWsChange = (rowIndex, key, value) => {
    setWsRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, [key]: value } : r)));
  };

  const getWsChangedRows = () => {
    const norm = (v) => String(v ?? "");
    return wsRows.filter((r, i) => {
      const o = wsOriginal[i];
      if (!o) return true;

      return (
        norm(r.work_system) !== norm(o.work_system) ||
        norm(r.start_time) !== norm(o.start_time) ||
        norm(r.end_time) !== norm(o.end_time)
      );
    });
  };

  const handleWsSave = async () => {
    const changed = getWsChangedRows();

    if (changed.length === 0) {
      Swal.fire("저장할 변경사항이 없습니다.", "", "info");
      return;
    }

    try {
      const res = await saveWorkSystemList(changed);
      const ok = res?.status === 200 || res?.data?.code === 200;

      if (!ok) {
        Swal.fire("저장 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      Swal.fire("저장 완료", "근무형태가 저장되었습니다.", "success");

      const latest = await fetchWorkSystemList({ snapshot: true });
      setWsRows(latest || []);
      setWsOriginal(latest || []);

      setWsOpen(false);
    } catch (err) {
      Swal.fire("저장 실패", err?.message || "오류", "error");
    }
  };

  // =========================
  // ✅ 근무형태 모달 우클릭 행삭제 로직
  // =========================
  // 우클릭 시 컨텍스트 메뉴 열기
  const handleWsContextMenu = (e, rowIndex, idx) => {
    e.preventDefault();
    setWsCtxMenu({ open: true, mouseX: e.clientX, mouseY: e.clientY, rowIndex, idx });
  };

  // 컨텍스트 메뉴 닫기
  const closeWsCtxMenu = () => setWsCtxMenu({ open: false, mouseX: 0, mouseY: 0, rowIndex: null, idx: null });

  // 행 삭제 처리: 신규 행(idx=null)은 로컬 제거, 기존 행은 백엔드 del_yn=Y 처리
  const handleWsDeleteRow = async () => {
    const { rowIndex, idx } = wsCtxMenu;
    closeWsCtxMenu();

    if (idx == null) {
      // 아직 저장 안 된 신규 행은 로컬에서만 제거
      setWsRows((prev) => prev.filter((_, i) => i !== rowIndex));
      setWsOriginal((prev) => prev.filter((_, i) => i !== rowIndex));
      return;
    }

    const confirm = await Swal.fire({
      title: "행 삭제",
      text: "해당 근무형태를 삭제하시겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await api.post("/Operate/AccountMemberWorkSystemDelete", { idx });
      const ok = res?.data?.code === 200;
      if (!ok) {
        Swal.fire("삭제 실패", res?.data?.message || "서버 오류", "error");
        return;
      }
      // 삭제 후 목록 새로고침 (del_yn=Y 행은 조회 제외)
      const latest = await fetchWorkSystemList({ snapshot: true });
      setWsRows(latest || []);
      setWsOriginal(latest || []);
    } catch (err) {
      Swal.fire("삭제 실패", err?.message || "오류", "error");
    }
  };

  // =========================
  // ✅ 통합/유틸 관리 모달 로직 
  // =========================
  const [utilOpen, setUtilOpen] = useState(false);

  const [utilMemberRows, setUtilMemberRows] = useState([]);
  const [utilSelectedMember, setUtilSelectedMember] = useState(null);

  const [utilMappingRows, setUtilMappingRows] = useState([]);
  const [utilSelectedMappingRowIndex, setUtilSelectedMappingRowIndex] = useState(null);
  // ✅ 매핑 목록(가운데) 우클릭 컨텍스트 메뉴 상태 (직책이 통합일 때만 "삭제 처리" 노출)
  const [utilMappingCtxMenu, setUtilMappingCtxMenu] = useState({
    open: false,
    mouseX: 0,
    mouseY: 0,
    rowIndex: null,
  });

  const [utilAccountRows, setUtilAccountRows] = useState([]);
  const [utilSelectedAccount, setUtilSelectedAccount] = useState(null);

  const fetchUtilMemberList = useCallback(async () => {
    const res = await api.get("/Account/AccountUtilMemberList");
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const fetchUtilMappingList = useCallback(async (memberId) => {
    if (!memberId) return [];
    const res = await api.get("/Account/AccountUtilMappingList", {
      params: { member_id: memberId },
    });
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const fetchUtilAccountList = useCallback(async () => {
    const res = await api.get("/Account/AccountList", {
      params: { account_type: 0 },
    });
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
  }, []);

  const openUtilModal = async () => {
    try {
      setUtilSelectedMember(null);
      setUtilSelectedAccount(null);
      setUtilSelectedMappingRowIndex(null);
      setUtilMappingRows([]);

      const [members, accounts] = await Promise.all([
        fetchUtilMemberList(),
        fetchUtilAccountList(),
      ]);
      setUtilMemberRows(members || []);
      setUtilAccountRows(accounts || []);

      setUtilOpen(true);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류", "error");
    }
  };

  const closeUtilModal = () => setUtilOpen(false);

  const handleSelectUtilMember = async (row) => {
    try {
      setUtilSelectedMember(row);
      setUtilSelectedMappingRowIndex(null);

      const memberId = row?.member_id;
      if (!memberId) {
        setUtilMappingRows([]);
        return;
      }

      const mappings = await fetchUtilMappingList(memberId);
      setUtilMappingRows(mappings || []);
    } catch (err) {
      Swal.fire("조회 실패", err?.message || "오류", "error");
    }
  };

  const handleAddMappingFromRight = () => {
    if (!utilSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 통합/유틸 직원을 먼저 선택해주세요.", "info");
      return;
    }
    if (!utilSelectedAccount?.account_id) {
      Swal.fire("안내", "오른쪽에서 업장을 먼저 선택해주세요.", "info");
      return;
    }

    const member_id = utilSelectedMember.member_id;
    const position_type = utilSelectedMember.position_type;
    const account_id = utilSelectedAccount.account_id;

    const exists = (utilMappingRows || []).some(
      (r) =>
        String(r.member_id) === String(member_id) && String(r.account_id) === String(account_id)
    );
    if (exists) {
      Swal.fire("안내", "이미 매핑된 업장입니다.", "info");
      return;
    }

    const newRow = {
      idx: null,
      account_id,
      member_id,
      name: utilSelectedMember.name ?? utilSelectedMember.member_name ?? "",
      position_type,
    };

    setUtilMappingRows((prev) => [newRow, ...(prev || [])]);
  };

  const handleUtilSave = async () => {
    if (!utilSelectedMember?.member_id) {
      Swal.fire("안내", "왼쪽에서 통합/유틸 직원을 먼저 선택해주세요.", "info");
      return;
    }

    try {
      const res = await api.post("/Account/AccountUtilMemberMappingSave", utilMappingRows);
      const ok = res?.status === 200 || res?.data?.code === 200;

      if (!ok) {
        Swal.fire("저장 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      Swal.fire("저장 완료", "통합/유틸 매핑이 저장되었습니다.", "success");

      const latest = await fetchUtilMappingList(utilSelectedMember.member_id);
      setUtilMappingRows(latest || []);
      setUtilOpen(false);
    } catch (err) {
      Swal.fire("저장 실패", err?.message || "오류", "error");
    }
  };

  // ✅ 매핑 목록(가운데) 우클릭 메뉴 열기/닫기 (직책 통합(7)인 행에서만 사용)
  const handleUtilMappingContextMenu = useCallback((e, rowIndex) => {
    const row = utilMappingRows?.[rowIndex];
    if (String(row?.position_type ?? "") !== INTEGRATION_POSITION) return;
    e.preventDefault();
    e.stopPropagation();
    setUtilMappingCtxMenu({ open: true, mouseX: e.clientX, mouseY: e.clientY, rowIndex });
  }, [utilMappingRows]);

  const closeUtilMappingCtxMenu = useCallback(() => {
    setUtilMappingCtxMenu((prev) => ({ ...prev, open: false, rowIndex: null }));
  }, []);

  // ✅ 통합/유틸 매핑 삭제 처리: tb_account_util_member_mapping.del_yn만 'Y'로 변경(직원 원본은 유지)
  const handleUtilMappingDelete = useCallback(async () => {
    const { rowIndex } = utilMappingCtxMenu;
    const targetRow = utilMappingRows?.[rowIndex];
    closeUtilMappingCtxMenu();
    if (!targetRow) return;

    // 아직 저장 안 된 신규 매핑 행은 로컬에서만 제거
    if (targetRow.idx == null) {
      setUtilMappingRows((prev) => prev.filter((_, i) => i !== rowIndex));
      return;
    }

    const confirm = await Swal.fire({
      title: "삭제 처리",
      text: "해당 매핑을 삭제 처리(제외) 하시겠습니까?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "삭제 처리",
      cancelButtonText: "취소",
    });
    if (!confirm.isConfirmed) return;

    try {
      const userId = localStorage.getItem("user_id");
      const res = await api.post("/Account/AccountUtilMemberMappingSave", [
        {
          idx: targetRow.idx,
          member_id: targetRow.member_id,
          account_id: targetRow.account_id,
          del_yn: "Y",
          user_id: userId,
        },
      ]);
      const ok = res?.status === 200 || res?.data?.code === 200;
      if (!ok) {
        Swal.fire("처리 실패", res?.data?.message || "서버 오류", "error");
        return;
      }

      Swal.fire("완료", "삭제 처리되었습니다.", "success");
      const latest = await fetchUtilMappingList(targetRow.member_id);
      setUtilMappingRows(latest || []);
    } catch (err) {
      Swal.fire("처리 실패", err?.message || "오류", "error");
    }
  }, [utilMappingCtxMenu, utilMappingRows, closeUtilMappingCtxMenu, fetchUtilMappingList]);

  // ✅ 행추가: 기본 직책 1.
  //    통합(7)은 account_id=1, 유틸(6)은 account_id=2 고정
  const handleAddRow = () => {
    lockColumnsForEditing();
    const defaultAccountId = selectedAccountId || (accountList?.[0]?.account_id ?? "");
    const defaultWorkSystemIdx = workSystemList?.[0]?.idx ? String(workSystemList[0].idx) : "";

    const defaultPositionType = "1"; // 신규 기본 직책
    const initAccountId =
      defaultPositionType === UTIL_POSITION
        ? UTIL_ACCOUNT_ID
        : defaultPositionType === INTEGRATION_POSITION
          ? INTEGRATION_ACCOUNT_ID
          : defaultAccountId;

    const newRow = {
      name: "",
      rrn: "",
      account_id: initAccountId,

      position_type: defaultPositionType,
      account_number: "",
      phone: "",
      address: "",
      contract_type: "1",
      join_dt: "",
      act_join_dt: "",
      ret_set_dt: "",
      loss_major_insurances: "",
      del_yn: activeStatus,
      del_dt: "",
      del_note: "",
      salary: "",
      idx: defaultWorkSystemIdx,
      start_time: workSystemList?.[0]?.start_time
        ? normalizeTime(workSystemList[0].start_time)
        : startTimes?.[0] ?? "5:30",
      end_time: workSystemList?.[0]?.end_time
        ? normalizeTime(workSystemList[0].end_time)
        : endTimes?.[0] ?? "10:00",
      national_pension: "",
      health_insurance: "",
      industrial_insurance: "",
      employment_insurance: "",
      note: "",
      headoffice_note: "",
      subsidy: "",
      total: 0,

      employment_contract: "",
      id: "",
      bankbook: "",
      cor_type: "1",
    };

    setActiveRows((prev) => [newRow, ...(prev || [])]);
    setOriginalRows((prev) => [newRow, ...(prev || [])]);
  };

  const renderTable = (table, rows, originals) => {
    const dateFields = new Set([
      "join_dt",
      "act_join_dt",
      "ret_set_dt",
      "loss_major_insurances",
      "del_dt",
    ]);
    const insuranceDateFields = new Set(insuranceDateFieldKeys);

    const selectFields = new Set([
      "position_type",
      "del_yn",
      "contract_type",
      "start_time",
      "end_time",
      "account_id",
      "idx",
      "cor_type",
    ]);

    const nonEditableCols = new Set(["diner_date", "total"]);
    // 화면 크기별 고정 열 범위
    // 모바일: 이름(2열)까지 고정, 태블릿: 주민번호(3열)까지 고정, 데스크탑: 기존(7열) 유지
    const stickyColumnCount = isMobile ? 2 : isTablet ? 3 : 7;
    // n번째 고정 열의 left 위치: 앞 열들의 실제 렌더링 너비 합 (측정 전에는 컬럼 size 기준)
    const getStickyLeft = (n) =>
      `${table
        .getFlatHeaders()
        .slice(0, n - 1)
        .reduce(
          (sum, h) => sum + (measuredColWidths[h.id] ?? h.column.columnDef.size ?? 80),
          0
        )}px`;

    return (
      <MDBox
        ref={tableContainerRef}
        pt={0}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: isMobile ? "55vh" : "75vh",
          overflowX: "auto",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          "& table": {
            borderCollapse: "separate",
            width: "max-content",
            minWidth: "100%",
            borderSpacing: 0,
            tableLayout: isColumnWidthLocked ? "fixed" : "auto",
          },
          "& th, & td": {
            border: "1px solid #686D76",
            textAlign: "center",
            padding: "4px",
            whiteSpace: "nowrap",
            fontSize: "12px",
            verticalAlign: "middle",
          },
          "& th": {
            backgroundColor: "#f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 2,
          },
          ...(stickyColumnCount >= 1
            ? {
              "& td:nth-of-type(1), & th:nth-of-type(1)": {
                position: "sticky",
                left: 0,
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 2
            ? {
              "& td:nth-of-type(2), & th:nth-of-type(2)": {
                position: "sticky",
                left: getStickyLeft(2),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 3
            ? {
              "& td:nth-of-type(3), & th:nth-of-type(3)": {
                position: "sticky",
                left: getStickyLeft(3),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 4
            ? {
              "& td:nth-of-type(4), & th:nth-of-type(4)": {
                position: "sticky",
                left: getStickyLeft(4),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 5
            ? {
              "& td:nth-of-type(5), & th:nth-of-type(5)": {
                position: "sticky",
                left: getStickyLeft(5),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 6
            ? {
              "& td:nth-of-type(6), & th:nth-of-type(6)": {
                position: "sticky",
                left: getStickyLeft(6),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 7
            ? {
              "& td:nth-of-type(7), & th:nth-of-type(7)": {
                position: "sticky",
                left: getStickyLeft(7),
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          [`thead th:nth-of-type(-n+${stickyColumnCount})`]: { zIndex: 5 },
          "& .edited-cell": { color: "#d32f2f", fontWeight: 500 },
          "td[contenteditable]": {
            minWidth: "80px",
            cursor: "text",
            ...(isColumnWidthLocked
              ? {
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }
              : {}),
          },
          "& td[contenteditable]::-webkit-scrollbar": { display: "none" },
          "& select": {
            fontSize: "12px",
            padding: "4px",
            minWidth: "80px",
            border: "none",
            background: "transparent",
            outline: "none",
            cursor: "pointer",
          },
          "& select.edited-cell": { color: "#d32f2f", fontWeight: 500 },
          "& input[type='date']": {
            fontSize: "12px",
            padding: "4px",
            minWidth: "80px",
            border: "none",
            background: "transparent",
          },
        }}
      >
        <table className="dinersheet-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const headerWidth =
                    header.column.columnDef.size ?? header.column.columnDef.minWidth ?? 80;
                  const lockedHeaderWidth = measuredColWidths[header.id] ?? headerWidth;
                  return (
                    <th
                      key={header.id}
                      style={{
                        width: isColumnWidthLocked ? lockedHeaderWidth : headerWidth,
                        ...(isColumnWidthLocked
                          ? { minWidth: lockedHeaderWidth, maxWidth: lockedHeaderWidth }
                          : {}),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const colKey = cell.column.columnDef.accessorKey;
                  const currentValue = row.getValue(colKey);
                  const cellWidth =
                    measuredColWidths[cell.column.id] ??
                    cell.column.columnDef.size ??
                    cell.column.columnDef.minWidth ??
                    80;
                  const originalValue = originals?.[rowIndex]?.[colKey];
                  const isNewRow = !String(row.original?.member_id ?? "").trim();

                  const isNumeric = numericCols.includes(colKey);
                  const isImage = imageFields.includes(colKey);

                  const normCurrent = isImage
                    ? typeof currentValue === "object" && currentValue
                      ? "__FILE__"
                      : String(currentValue ?? "")
                    : isNumeric
                      ? Number(currentValue ?? 0)
                      : String(currentValue ?? "");

                  const normOriginal = isImage
                    ? String(originalValue ?? "")
                    : isNumeric
                      ? Number(originalValue ?? 0)
                      : String(originalValue ?? "");

                  const isChanged = normCurrent !== normOriginal;

                  const isEditable = !nonEditableCols.has(colKey);
                  const isSelect = selectFields.has(colKey);
                  const isDate = dateFields.has(colKey);
                  const isInsuranceDate = insuranceDateFields.has(colKey);
                  const canEditMaskedSensitiveField = canEditSensitiveField(
                    colKey,
                    currentValue,
                    maskingEnabled,
                    {},
                    { isNewRow }
                  );

                  const handleCellChange = (newValue) => {
                    lockColumnsForEditing();
                    const updatedRows = rows.map((r, idx) => {
                      if (idx !== rowIndex) return r;

                      // ✅ 직책 변경 시: 통합(7)=1, 유틸(6)=2 강제
                      if (colKey === "position_type") {
                        const nextPos = String(newValue);
                        const nextAccount =
                          nextPos === UTIL_POSITION
                            ? UTIL_ACCOUNT_ID
                            : nextPos === INTEGRATION_POSITION
                              ? INTEGRATION_ACCOUNT_ID
                              : [UTIL_ACCOUNT_ID, INTEGRATION_ACCOUNT_ID].includes(
                                String(r.account_id ?? "")
                              )
                                ? selectedAccountId || (accountList?.[0]?.account_id ?? "")
                                : String(r.account_id ?? "");

                        return {
                          ...r,
                          position_type: newValue,
                          account_id: nextAccount,
                          total: calculateTotal({ ...r, position_type: newValue }),
                        };
                      }

                      // ✅ work_system 변경 시 start/end 자동 세팅
                      if (colKey === "idx") {
                        const selected = (workSystemList || []).find(
                          (w) => String(w.idx) === String(newValue)
                        );

                        return {
                          ...r,
                          idx: newValue,
                          start_time: selected?.start_time
                            ? normalizeTime(selected.start_time)
                            : r.start_time,
                          end_time: selected?.end_time
                            ? normalizeTime(selected.end_time)
                            : r.end_time,
                          total: calculateTotal({
                            ...r,
                            idx: newValue,
                            start_time: selected?.start_time
                              ? normalizeTime(selected.start_time)
                              : r.start_time,
                            end_time: selected?.end_time
                              ? normalizeTime(selected.end_time)
                              : r.end_time,
                          }),
                        };
                      }

                      return {
                        ...r,
                        [colKey]: newValue,
                        total: calculateTotal({ ...r, [colKey]: newValue }),
                      };
                    });

                    // ✅ 혹시 모를 케이스 대비: 한번 더 유틸 account_id 보정
                    setActiveRows(applyUtilAccountId(updatedRows));
                  };

                  // ✅ 이미지 컬럼
                  if (isImage) {
                    const value = currentValue ?? "";
                    const hasImage = !!value;
                    const inputId = `upload-${colKey}-${rowIndex}`;

                    return (
                      <td
                        key={cell.id}
                        className={isChanged ? "edited-cell" : ""}
                        style={{
                          textAlign: "center",
                          ...(isColumnWidthLocked
                            ? { width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth }
                            : {}),
                        }}
                      >
                        <input
                          type="file"
                          accept={HEADOFFICE_DOCUMENT_FILE_ACCEPT}
                          id={inputId}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) {
                              e.target.value = null;
                              return;
                            }
                            if (!isHeadOfficeDocumentSupportedFile(file)) {
                              Swal.fire(
                                "지원하지 않는 파일 형식",
                                "이미지, PDF, Excel(xls/xlsx) 파일만 첨부할 수 있습니다.",
                                "warning"
                              );
                              e.target.value = null;
                              return;
                            }
                            handleCellChange(file);
                            e.target.value = null;
                          }}
                        />

                        {hasImage ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              flexWrap: "nowrap",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeof value === "string" && (
                              <Tooltip title="다운로드">
                                <IconButton
                                  size="small"
                                  sx={{ ...fileIconSx, p: 0.5 }}
                                  onClick={() => handleDownload(value)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title="미리보기">
                              <IconButton
                                size="small"
                                sx={{ ...fileIconSx, color: isChanged ? "#d32f2f" : fileIconSx.color, p: 0.5 }}
                                onClick={() => handleViewImage(row.original, colKey)}
                              >
                                <ImageSearchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <label htmlFor={inputId}>
                              <MDButton
                                size="small"
                                component="span"
                                color="info"
                                sx={{
                                  fontSize: isMobile ? "10px" : "11px",
                                  minWidth: 44,
                                  px: 1,
                                  py: 0.5,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                변경
                              </MDButton>
                            </label>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              flexWrap: "nowrap",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <label htmlFor={inputId} style={{ display: "inline-flex" }}>
                              <MDButton
                                size="small"
                                component="span"
                                color="info"
                                sx={{
                                  fontSize: isMobile ? "10px" : "11px",
                                  minWidth: 44,
                                  px: 1,
                                  py: 0.5,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                업로드
                              </MDButton>
                            </label>
                          </div>
                        )}
                      </td>
                    );
                  }

                  return (
                    <td
                      key={cell.id}
                      style={{
                        ...(isColumnWidthLocked
                          ? { width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth }
                          : {}),
                        textAlign: [
                          "rrn",
                          "account_number",
                          "phone",
                          "contract_type",
                          "join_dt",
                          "act_join_dt",
                          "ret_set_dt",
                          "loss_major_insurances",
                          "del_yn",
                          "del_dt",
                          "idx",
                          "start_time",
                          "end_time",
                          "national_pension",
                          "health_insurance",
                          "industrial_insurance",
                          "employment_insurance",
                        ].includes(colKey)
                          ? "center"
                          : colKey === "salary"
                            ? "right"
                            : "left",
                      }}
                      contentEditable={
                        isEditable &&
                        !isSelect &&
                        !isDate &&
                        !isInsuranceDate &&
                        canEditMaskedSensitiveField
                      }
                      suppressContentEditableWarning
                      className={isEditable && isChanged ? "edited-cell" : ""}
                      onFocus={
                        isEditable &&
                          !isSelect &&
                          !isDate &&
                          !isInsuranceDate &&
                          canEditMaskedSensitiveField
                          ? (e) => {
                            lockColumnsForEditing();
                            keepEditableTailVisible(e.currentTarget);
                          }
                          : undefined
                      }
                      onInput={
                        isEditable &&
                          !isSelect &&
                          !isDate &&
                          !isInsuranceDate &&
                          canEditMaskedSensitiveField
                          ? (e) => {
                            keepEditableTailVisible(e.currentTarget);
                          }
                          : undefined
                      }
                      onBlur={
                        isEditable &&
                          !isSelect &&
                          !isDate &&
                          !isInsuranceDate &&
                          canEditMaskedSensitiveField
                          ? (e) => {
                            let newValue = e.target.innerText.trim();
                            if (isNumeric) newValue = parseNumber(newValue);
                            handleCellChange(newValue);

                            if (isNumeric) {
                              e.currentTarget.innerText = formatNumber(newValue);
                            }
                          }
                          : undefined
                      }
                    >
                      {isInsuranceDate ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                          }}
                        >
                          <select
                            value={getInsuranceStatus(currentValue)}
                            onChange={(e) => {
                              const next = e.target.value;
                              if (next === "N") {
                                handleCellChange("미해당");
                                return;
                              }
                              if (next === "Y") {
                                const existingDate = formatDateForInput(currentValue);
                                handleCellChange(existingDate || dayjs().format("YYYY-MM-DD"));
                                return;
                              }
                              handleCellChange("");
                            }}
                            className={isChanged ? "edited-cell" : ""}
                            style={{
                              minWidth: 68,
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                            }}
                          >
                            <option value="">선택</option>
                            <option value="Y">해당</option>
                            <option value="N">미해당</option>
                          </select>

                          {getInsuranceStatus(currentValue) === "Y" && (
                            <input
                              type="date"
                              value={formatDateForInput(currentValue)}
                              onChange={(e) => handleCellChange(e.target.value)}
                              className={isChanged ? "edited-cell" : ""}
                            />
                          )}
                        </div>
                      ) : isSelect ? (
                        colKey === "idx" ? (
                          <Autocomplete
                            size="small"
                            options={(workSystemList || []).map((w) => ({
                              value: String(w.idx),
                              label: w.work_system,
                            }))}
                            value={(() => {
                              const v = String(currentValue ?? "");
                              return (
                                (workSystemList || [])
                                  .map((w) => ({ value: String(w.idx), label: w.work_system }))
                                  .find((o) => o.value === v) || null
                              );
                            })()}
                            onChange={(_, opt) => handleCellChange(opt ? opt.value : "")}
                            getOptionLabel={(opt) => opt?.label ?? ""}
                            isOptionEqualToValue={(opt, val) => opt.value === val.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="검색"
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    fontSize: "12px",
                                    padding: 0,
                                    color: isChanged ? "#d32f2f" : "inherit",
                                    fontWeight: isChanged ? 600 : 400,
                                  },
                                }}
                              />
                            )}
                            sx={{
                              width: "100%",
                              "& .MuiInputBase-root": { minHeight: 24 },
                              "& .MuiAutocomplete-input": {
                                fontSize: "12px",
                                padding: "0px !important",
                                color: isChanged ? "#d32f2f" : "inherit",
                                fontWeight: isChanged ? 600 : 400,
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 18,
                                color: isChanged ? "#d32f2f" : "inherit",
                              },
                              "& .MuiAutocomplete-option": { fontSize: "12px", minHeight: 28 },
                            }}
                            ListboxProps={{ style: { fontSize: "12px" } }}
                          />
                        ) : colKey === "account_id" ? (
                          (() => {
                            const isAccountChanged =
                              String(currentValue ?? "") !== String(originalValue ?? "");

                            return (
                              <Autocomplete
                                size="small"
                                options={accountOptions}
                                value={(() => {
                                  const v = String(currentValue ?? "");
                                  return accountOptions.find((o) => o.value === v) || null;
                                })()}
                                onChange={(_, opt) => handleCellChange(opt ? opt.value : "")}
                                getOptionLabel={(opt) => opt?.label ?? ""}
                                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="검색"
                                    InputProps={{
                                      ...params.InputProps,
                                      disableUnderline: true,
                                    }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: {
                                        fontSize: "12px",
                                        padding: 0,
                                        color: isAccountChanged ? "#d32f2f" : "inherit",
                                        fontWeight: isAccountChanged ? 600 : 400,
                                      },
                                    }}
                                  />
                                )}
                                sx={{
                                  minWidth: 180,
                                  "& .MuiInputBase-root": { minHeight: 24 },
                                  "& .MuiAutocomplete-input": {
                                    fontSize: "12px",
                                    padding: "0px !important",
                                    color: isAccountChanged ? "#d32f2f" : "inherit",
                                    fontWeight: isAccountChanged ? 600 : 400,
                                  },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 18,
                                    color: isAccountChanged ? "#d32f2f" : "inherit",
                                  },
                                  "& .MuiAutocomplete-option": {
                                    fontSize: "12px",
                                    minHeight: 28,
                                  },
                                }}
                                ListboxProps={{ style: { fontSize: "12px" } }}
                              />
                            );
                          })()
                        ) : (
                          <select
                            value={currentValue ?? ""}
                            onChange={(e) => handleCellChange(e.target.value)}
                            className={isChanged ? "edited-cell" : ""}
                            style={{
                              width: "100%",
                              background: "transparent",
                              cursor: "pointer",
                              border: "none",
                            }}
                          >
                            {colKey === "cor_type" &&
                              corOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "del_yn" &&
                              delOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "position_type" &&
                              positionOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "contract_type" &&
                              contractOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}

                            {colKey === "start_time" && (
                              <>
                                <option value="">없음</option>
                                {startTimes.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </>
                            )}

                            {colKey === "end_time" && (
                              <>
                                <option value="">없음</option>
                                {endTimes.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </>
                            )}
                          </select>
                        )
                      ) : isDate ? (
                        <input
                          type="date"
                          value={formatDateForInput(currentValue)}
                          onChange={(e) => handleCellChange(e.target.value)}
                          className={isChanged ? "edited-cell" : ""}
                        />
                      ) : (
                        (isNumeric
                          ? formatNumber(currentValue)
                          : maskSensitiveFieldValue(colKey, currentValue, maskingEnabled)) ?? ""
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </MDBox>
    );
  };

  // 통합/유틸 관리 모달에서 업장명 표시용 맵
  const utilAccountNameMap = useMemo(() => {
    const m = new Map();
    (utilAccountRows || []).forEach((a) => {
      m.set(String(a.account_id), a.account_name);
    });
    return m;
  }, [utilAccountRows]);

  if (loading) return <LoadingScreen />;

  return (
    <>
      <MDBox
        pt={1}
        pb={1}
        sx={{
          display: "flex",
          justifyContent: isTabletOrSmallDesktop ? "flex-start" : "flex-end",
          alignItems: "center",
          gap: isTabletOrSmallDesktop ? 1 : 2,
          flexWrap: isTabletOrSmallDesktop ? "wrap" : "nowrap",
          position: "sticky",
          zIndex: 10,
          top: 78,
          backgroundColor: "#ffffff",
        }}
      >
        <TextField
          select
          size="small"
          value={activeStatus}
          onChange={(e) => {
            setLoading(true);
            setActiveStatus(e.target.value);
          }}
          sx={{ minWidth: 150 }}
          SelectProps={{ native: true }}
        >
          <option value="N">재직자</option>
          <option value="Y">퇴사자</option>
        </TextField>

        <TextField
          size="small"
          value={memberInput}
          onChange={(e) => setMemberInput(e.target.value)}
          label="직원 검색"
          placeholder="직원명을 입력"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              selectMemberByInput();
            }
          }}
          sx={{
            minWidth: 200,
            ...(isTabletOrSmallDesktop ? { flex: "1 1 220px" } : {}),
            "& .MuiInputBase-root": { height: 35, fontSize: 12 },
            "& .MuiInputLabel-root": { fontSize: 12 },
            "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
          }}
        />

        <Autocomplete
          size="small"
          sx={{
            minWidth: 200,
            ...(isTabletOrSmallDesktop ? { flex: "1 1 220px" } : {}),
          }}
          options={accountOptions}
          value={selectedAccountOption}
          onChange={(_, opt) => {
            // 입력 비움 시 거래처 선택 유지
            if (!opt) return;
            // ✅ 거래처를 직접 선택하면 자동 reset 차단은 해제
            suppressAccountResetRef.current = false;
            setLoading(true);
            setMemberInput("");
            setMemberSearchName("");
            setSelectedAccountId(opt.value);
          }}
          inputValue={accountInput}
          onInputChange={(_, newValue, reason) => {
            // ✅ 직원 검색 직후 발생하는 내부 reset 이벤트는 무시
            if (reason === "reset" && suppressAccountResetRef.current) return;
            if (reason === "input" || reason === "clear") {
              suppressAccountResetRef.current = false;
            }
            setAccountInput(newValue);
          }}
          getOptionLabel={(opt) => opt?.label ?? ""}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          filterOptions={(options, state) => {
            const q = (state.inputValue ?? "").trim().toLowerCase();
            if (!q) return options;
            return options.filter((o) => (o.label ?? "").toLowerCase().includes(q));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="거래처 검색"
              placeholder="거래처명을 입력"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  selectAccountByInput();
                }
              }}
              sx={{
                "& .MuiInputBase-root": { height: 35, fontSize: 12 },
                "& .MuiInputLabel-root": { fontSize: 12 },
                "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
              }}
            />
          )}
        />

        <MDButton variant="gradient" color="warning" onClick={openWorkSystemModal}>
          근무형태 관리
        </MDButton>

        <MDButton variant="gradient" color="warning" onClick={openUtilModal}>
          통합/유틸 관리
        </MDButton>

        <MDButton
          variant="outlined"
          color={maskingEnabled ? "dark" : "secondary"}
          onClick={handleToggleMasking}
        >
          {maskingEnabled ? "* 해제" : "* 적용"}
        </MDButton>

        <MDButton variant="gradient" color="success" onClick={handleAddRow}>
          행추가
        </MDButton>

        <MDButton variant="gradient" color="info" onClick={handleSave}>
          저장
        </MDButton>
      </MDBox>

      <MDBox pt={1} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {renderTable(table, activeRows, originalRows)}
          </Grid>
        </Grid>
      </MDBox>

      {/* =========================
          ✅ 근무형태 모달(기존 그대로)
         ========================= */}
      <Modal open={wsOpen} onClose={closeWorkSystemModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "95vw" : 720,
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              bgcolor: "#fff",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <MDTypography variant="h6">근무형태 관리</MDTypography>

            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="success" onClick={handleWsAddRow}>
                행추가
              </MDButton>
              <MDButton variant="gradient" color="info" onClick={handleWsSave}>
                저장
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={closeWorkSystemModal}>
                닫기
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              bgcolor: "#fff",
            }}
          >
            <MDBox
              sx={{
                p: 2,
                "& table": {
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                },
                "& th, & td": {
                  border: "1px solid #686D76",
                  padding: "6px",
                  fontSize: "12px",
                  textAlign: "center",
                  backgroundColor: "#fff",
                },
                "& thead th": {
                  position: "sticky",
                  top: 0,
                  zIndex: 20,
                  backgroundColor: "#f0f0f0",
                  boxShadow: "0 1px 0 rgba(0,0,0,0.12)",
                  backgroundClip: "padding-box",
                },
                "& input, & select": {
                  width: "100%",
                  fontSize: "12px",
                  padding: "6px",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                },
                "& .edited-cell": { color: "#d32f2f", fontWeight: 600 },
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>순번</th>
                    <th>근무형태명</th>
                    <th style={{ width: 140 }}>시작</th>
                    <th style={{ width: 140 }}>마감</th>
                  </tr>
                </thead>

                <tbody>
                  {(wsRows || []).map((r, i) => {
                    const o = wsOriginal?.[i] || {};
                    const isNewRow = r.idx == null || !wsOriginal?.[i];

                    const changedWorkSystem =
                      String(r.work_system ?? "") !== String(o.work_system ?? "");
                    const changedStartTime =
                      String(r.start_time ?? "") !== String(o.start_time ?? "");
                    const changedEndTime = String(r.end_time ?? "") !== String(o.end_time ?? "");

                    return (
                      <tr
                        key={`${r.idx ?? "new"}-${i}`}
                        className={isNewRow ? "edited-cell" : ""}
                        onContextMenu={(e) => handleWsContextMenu(e, i, r.idx)}
                        style={{ cursor: "context-menu" }}
                      >
                        <td className={isNewRow ? "edited-cell" : ""}>{i + 1}</td>

                        <td className={isNewRow || changedWorkSystem ? "edited-cell" : ""}>
                          <input
                            value={r.work_system ?? ""}
                            onChange={(e) => handleWsChange(i, "work_system", e.target.value)}
                            placeholder="예) 주5일(09~18)"
                            className={isNewRow || changedWorkSystem ? "edited-cell" : ""}
                          />
                        </td>

                        <td className={isNewRow || changedStartTime ? "edited-cell" : ""}>
                          <select
                            value={r.start_time ?? ""}
                            onChange={(e) => handleWsChange(i, "start_time", e.target.value)}
                            className={isNewRow || changedStartTime ? "edited-cell" : ""}
                          >
                            <option value="">없음</option>
                            {startTimes.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className={isNewRow || changedEndTime ? "edited-cell" : ""}>
                          <select
                            value={r.end_time ?? ""}
                            onChange={(e) => handleWsChange(i, "end_time", e.target.value)}
                            className={isNewRow || changedEndTime ? "edited-cell" : ""}
                          >
                            <option value="">없음</option>
                            {endTimes.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Box>
      </Modal>

      {/* =========================
          ✅ 근무형태 모달 우클릭 컨텍스트 메뉴
         ========================= */}
      {wsCtxMenu.open && (
        <div
          onClick={closeWsCtxMenu}
          onContextMenu={(e) => { e.preventDefault(); closeWsCtxMenu(); }}
          style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 10000 }}
        >
          <div
            style={{
              position: "absolute",
              top: wsCtxMenu.mouseY,
              left: wsCtxMenu.mouseX,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              minWidth: 140,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontSize: 13,
              }}
              onClick={handleWsDeleteRow}
            >
              🗑️ 행 삭제
            </button>
          </div>
        </div>
      )}

      {/* =========================
          ✅ 통합/유틸 관리 모달 (네 코드 그대로)
         ========================= */}
      <Modal open={utilOpen} onClose={closeUtilModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "98vw" : "92vw",
            maxWidth: 1200,
            height: isMobile ? "90vh" : "80vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              bgcolor: "#fff",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <MDTypography variant="h6">통합/유틸 관리</MDTypography>

            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="info" onClick={handleUtilSave}>
                저장
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={closeUtilModal}>
                닫기
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              display: "flex",
              gap: 1,
              p: 1.5,
              overflow: "hidden",
              bgcolor: "#fff",
              "& .edited-cell": { color: "#d32f2f", fontWeight: 600 },
            }}
          >
            {/* 왼쪽 테이블 */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 240,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  통합/유틸 직원 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 클릭 → 가운데 매핑 조회
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "#f7f7f7",
                    zIndex: 2,
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>성명</th>
                      <th style={{ width: 90 }}>직책</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utilMemberRows || []).map((r, i) => {
                      const selected =
                        String(utilSelectedMember?.member_id ?? "") === String(r.member_id ?? "");
                      const posLabel =
                        positionOptions.find((p) => String(p.value) === String(r.position_type))
                          ?.label ?? String(r.position_type ?? "");

                      return (
                        <tr
                          key={`${r.member_id ?? "m"}-${i}`}
                          onClick={() => handleSelectUtilMember(r)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                          }}
                        >
                          <td style={{ fontWeight: selected ? 700 : 400 }}>{r.name ?? ""}</td>
                          <td style={{ fontWeight: selected ? 700 : 400 }}>{posLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>

            {/* 가운데 테이블 */}
            <MDBox
              sx={{
                flex: 1.2,
                minWidth: 360,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  매핑 목록 (가운데)
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  선택된 통합/유틸:{" "}
                  <b>{utilSelectedMember?.member_id ? utilSelectedMember.member_id : "-"}</b>
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "#f7f7f7",
                    zIndex: 2,
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>순번</th>
                      <th style={{ width: 120 }}>고객사</th>
                      <th style={{ width: 110 }}>성명</th>
                      <th style={{ width: 90 }}>직책</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utilMappingRows || []).map((r, i) => {
                      const selected = utilSelectedMappingRowIndex === i;
                      const isNewUtilRow = r?.idx == null;
                      const posLabel =
                        positionOptions.find((p) => String(p.value) === String(r.position_type))
                          ?.label ?? String(r.position_type ?? "");
                      const accName = utilAccountNameMap.get(String(r.account_id ?? "")) ?? "";
                      const accText = accName;

                      return (
                        <tr
                          key={`${r.idx ?? "new"}-${r.account_id ?? "a"}-${i}`}
                          onClick={() => setUtilSelectedMappingRowIndex(i)}
                          onContextMenu={(e) => handleUtilMappingContextMenu(e, i)}
                          className={isNewUtilRow ? "edited-cell" : ""}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(255,193,7,0.12)" : "#fff",
                          }}
                        >
                          <td className={isNewUtilRow ? "edited-cell" : ""}>{r.idx ?? ""}</td>
                          <td
                            className={isNewUtilRow ? "edited-cell" : ""}
                            style={{ textAlign: "left" }}
                          >
                            {accText}
                          </td>
                          <td className={isNewUtilRow ? "edited-cell" : ""}>{r.name ?? ""}</td>
                          <td className={isNewUtilRow ? "edited-cell" : ""}>{posLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>

            {/* 가운데-오른쪽 컨트롤 */}
            <MDBox
              sx={{
                width: isMobile ? 54 : 70,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleAddMappingFromRight}
                sx={{ minWidth: isMobile ? 46 : 56, px: 0 }}
              >
                {"<"}
              </MDButton>
              <MDTypography variant="caption" sx={{ color: "#666", textAlign: "center" }}>
                업장 → 매핑
              </MDTypography>
            </MDBox>

            {/* 오른쪽 테이블 */}
            <MDBox
              sx={{
                flex: 1,
                minWidth: 300,
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                <MDTypography variant="button" fontWeight="bold">
                  업장 목록
                </MDTypography>
                <MDTypography variant="caption" sx={{ display: "block", color: "#666" }}>
                  행 선택 후 &lt; 버튼
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  flex: 1,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    borderBottom: "1px solid #eee",
                    padding: "8px 6px",
                    fontSize: 12,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "#f7f7f7",
                    zIndex: 2,
                  },
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>고객사</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utilAccountRows || []).map((r, i) => {
                      const selected =
                        String(utilSelectedAccount?.account_id ?? "") ===
                        String(r.account_id ?? "");
                      return (
                        <tr
                          key={`${r.account_id ?? "a"}-${i}`}
                          onClick={() => setUtilSelectedAccount(r)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: selected ? "rgba(30,136,229,0.10)" : "#fff",
                          }}
                        >
                          <td style={{ textAlign: "left", fontWeight: selected ? 700 : 400 }}>
                            {r.account_name ?? ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>
          </MDBox>
        </Box>
      </Modal>

      {/* ✅ 통합/유틸 매핑 목록(가운데) 우클릭 메뉴 - 직책이 통합일 때만 노출 */}
      {utilMappingCtxMenu.open && (
        <div
          onClick={closeUtilMappingCtxMenu}
          onContextMenu={(e) => { e.preventDefault(); closeUtilMappingCtxMenu(); }}
          style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 10000 }}
        >
          <div
            style={{
              position: "absolute",
              top: utilMappingCtxMenu.mouseY,
              left: utilMappingCtxMenu.mouseX,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              minWidth: 140,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontSize: 13,
              }}
              onClick={handleUtilMappingDelete}
            >
              🗑️ 삭제 처리
            </button>
          </div>
        </div>
      )}

      {/* 첨부파일 공통 미리보기 오버레이 */}
      <PreviewOverlay
        open={previewFiles.length > 0}
        files={previewFiles}
        currentIndex={previewIndex}
        onChangeIndex={setPreviewIndex}
        onClose={handleCloseViewer}
      />
    </>
  );
}

export default AccountMemberSheet;
